'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  LegacyDialog,
  Button,
  Checkbox,
  IconButton,
  LabelDefault,
  Text,
} from '@terra/ui-design-system';
import { VStack, HStack } from '@terra/ui-styled-system';
import {
  appendGrpcWebResponseMimeTypes,
  getTorchTrackingMimeTypes,
  resolveDiscoveryContentTypes,
} from '@terra/shared-asset-processing';
import {
  GRPC_WEB_SUPPORT_ENABLED_FLAG,
  SKIP_NOISE_URL_EXTENSIONS_FLAG,
} from '@terra/shared-contracts-feature-flags';
import { Cancel01Icon } from '@terra/ui-icons';
import { useProjectStore } from '../../store/projectStore';
import { useApplicationById } from '../../hooks/api/useApplicationById';
import {
  ApplicationCredentialSelect,
  ApplicationCredentialSelectLabel,
} from './ApplicationCredentialSelect';
import { SessionContentTypesSection } from './SessionContentTypesSection';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

interface StartTrackingDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStartTracking: (config: TrackingConfig) => void;
}

export interface TrackingConfig {
  recordNetworkTraffic: boolean;
  recordPageScreenshots: boolean;
  credentialId?: string;
  responseMimeTypes: string[];
  skipNoiseUrlExtensions?: boolean;
}
export function StartTrackingDialog({
  isOpen,
  onOpenChange,
  onStartTracking,
}: StartTrackingDialogProps) {
  const t = useTranslations('proxy.tracking.configureTrackingDialog');
  const tCommon = useTranslations('common');
  const [recordNetworkTraffic, setRecordNetworkTraffic] = useState(true);
  const [recordPageScreenshots, setRecordPageScreenshots] = useState(true);
  const [selectedCredentialId, setSelectedCredentialId] = useState<string>('');
  const [responseMimeTypes, setResponseMimeTypes] = useState<string[]>([]);
  const { selectedTarget } = useProjectStore();
  const isBackendOnlyApplication = selectedTarget?.isBackendOnly === true;
  const { isFeatureFlagEnabled: isGrpcWebSupportEnabled } = useFeatureFlags(
    GRPC_WEB_SUPPORT_ENABLED_FLAG,
  );
  const { isFeatureFlagEnabled: isSkipNoiseUrlExtensionsEnabled } =
    useFeatureFlags(SKIP_NOISE_URL_EXTENSIONS_FLAG);
  const { application } = useApplicationById({
    id: selectedTarget?.id,
    enabled: isOpen && Boolean(selectedTarget?.id),
  });
  const applicationDiscoveryContentTypes = useMemo(
    () =>
      resolveDiscoveryContentTypes(
        application?.discoveryContentTypes ??
          selectedTarget?.discoveryContentTypes,
      ),
    [application?.discoveryContentTypes, selectedTarget?.discoveryContentTypes],
  );
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;

    wasOpenRef.current = isOpen;
    if (!justOpened) {
      return;
    }

    setRecordNetworkTraffic(true);
    setRecordPageScreenshots(isBackendOnlyApplication ? false : true);
    setSelectedCredentialId('');
    setResponseMimeTypes(
      appendGrpcWebResponseMimeTypes({
        mimeTypes: getTorchTrackingMimeTypes(applicationDiscoveryContentTypes),
        grpcWebSupportEnabled: isGrpcWebSupportEnabled,
      }),
    );
  }, [
    isOpen,
    isBackendOnlyApplication,
    applicationDiscoveryContentTypes,
    isGrpcWebSupportEnabled,
  ]);

  const handleConfirm = () => {
    onStartTracking({
      recordNetworkTraffic,
      recordPageScreenshots,
      credentialId: selectedCredentialId || undefined,
      responseMimeTypes,
      skipNoiseUrlExtensions: isSkipNoiseUrlExtensionsEnabled || undefined,
    });
    onOpenChange(false);
  };

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => onOpenChange(open)}
      modal
      closeOnInteractOutside={false}
      closeOnEscape={false}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md" w="full">
          <VStack gap="4" p="4">
            <VStack gap="1" w="full" alignItems="flex-start">
              <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
              <LegacyDialog.Description color="fg.muted">
                {t('description')}
              </LegacyDialog.Description>
            </VStack>

            <VStack gap="2" w="full" alignItems="flex-start">
              <ApplicationCredentialSelectLabel />
              <ApplicationCredentialSelect
                value={selectedCredentialId}
                onValueChange={setSelectedCredentialId}
                skip={!isOpen}
              />
            </VStack>

            <VStack gap="1" w="full" alignItems="flex-start">
              <LabelDefault fontWeight="semibold" color="fg.muted">
                {t('trackingOptions')}
              </LabelDefault>

              <VStack gap="1" w="full" alignItems="flex-start">
                <Checkbox
                  size="sm"
                  checked={recordNetworkTraffic}
                  onCheckedChange={(checked) =>
                    setRecordNetworkTraffic(!!checked.checked)
                  }
                >
                  <VStack gap="0" alignItems="flex-start">
                    <Text fontSize="xs" fontWeight="medium">
                      {t('recordNetworkTraffic')}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {t('recordNetworkTrafficDescription')}
                    </Text>
                  </VStack>
                </Checkbox>

                <Checkbox
                  size="sm"
                  checked={recordPageScreenshots}
                  disabled={isBackendOnlyApplication}
                  cursor={isBackendOnlyApplication ? 'not-allowed' : 'default'}
                  onCheckedChange={(checked) =>
                    setRecordPageScreenshots(!!checked.checked)
                  }
                >
                  <VStack gap="0" alignItems="flex-start">
                    <Text fontSize="xs" fontWeight="medium">
                      {t('recordPageScreenshots')}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {t('recordPageScreenshotsDescription')}
                    </Text>
                  </VStack>
                </Checkbox>
              </VStack>

              {!recordNetworkTraffic && !recordPageScreenshots && (
                <Text fontSize="xs" color="fg.error">
                  {t('selectAtLeastOneOption')}
                </Text>
              )}
            </VStack>

            {recordNetworkTraffic ? (
              <SessionContentTypesSection
                value={responseMimeTypes}
                onChange={setResponseMimeTypes}
                applicationConfigs={applicationDiscoveryContentTypes}
                onNavigateToSettings={() => onOpenChange(false)}
              />
            ) : null}

            <HStack gap="2" w="full" justifyContent="flex-end">
              <LegacyDialog.CloseTrigger asChild>
                <Button variant="outline" size="xs">
                  {tCommon('cancel')}
                </Button>
              </LegacyDialog.CloseTrigger>
              <Button
                size="xs"
                onClick={handleConfirm}
                disabled={
                  (!recordNetworkTraffic && !recordPageScreenshots) ||
                  (recordNetworkTraffic && responseMimeTypes.length === 0)
                }
              >
                {t('enableTracking')}
              </Button>
            </HStack>
          </VStack>

          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton aria-label={tCommon('close')} variant="ghost" size="xs">
              <Cancel01Icon size={14} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
